import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SectionBadge } from '@/components/landing/section-badge'

const SECTIONS = [
    { id: 'privacy', label: 'Privacy' },
    { id: 'cookies', label: 'Cookies and analytics' },
    { id: 'terms', label: 'Terms of use' }
]

const headingClass = 'scroll-mt-32 font-heading text-[28px] leading-tight tracking-[-0.025em] text-ink'
const paragraphClass = 'mt-4 text-[16.5px] leading-relaxed text-ink/80'
const listClass = 'mt-4 list-disc space-y-2 pl-5 text-[16.5px] leading-relaxed text-ink/80 marker:text-moss'

const Legal: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Legal'
                description='How Fieldhand handles the information you share with us, and the terms for using this site.'
            />

            <article className='mx-auto w-full max-w-3xl px-5 pt-36 pb-24 sm:pt-48 sm:pb-32'>
                <SectionBadge>Legal</SectionBadge>

                <h1 className='mt-6 font-heading text-[clamp(2.4rem,4.4vw,3.4rem)] leading-[1.08] tracking-[-0.03em] text-ink'>
                    Privacy and terms
                </h1>

                <p className='mt-4 text-[15px] text-muted-foreground'>
                    Last updated September 30, 2026
                </p>

                <nav aria-label='On this page' className='mt-10 rounded-[26px] bg-mist p-6'>
                    <p className='text-[13px] font-medium text-muted-foreground'>
                        On this page
                    </p>

                    <ul className='mt-3 flex flex-wrap gap-2'>
                        {SECTIONS.map((section) => (
                            <li key={section.id}>
                                <a
                                    href={`#${section.id}`}
                                    className='inline-flex rounded-full border border-border bg-white px-4 py-2 text-[14px] font-medium text-ink transition-colors hover:bg-sage focus-visible:ring-2 focus-visible:ring-moss focus-visible:outline-none'
                                >
                                    {section.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <section aria-labelledby='privacy' className='mt-16'>
                    <h2 id='privacy' className={headingClass}>
                        Privacy
                    </h2>

                    <p className={paragraphClass}>
                        When you start a free trial, we ask for your name and work email, and optionally your company and team size. We use this information to set up your workspace and to contact you about your trial. Nothing else is collected through the form.
                    </p>

                    <ul className={listClass}>
                        <li>We never sell your information or share it for advertising.</li>
                        <li>Your details are stored securely with our hosting provider and kept only as long as you have an active trial or account with us.</li>
                        <li>You can ask us to show, correct or delete your information at any time by replying to your workspace invite.</li>
                    </ul>
                </section>

                <section aria-labelledby='cookies' className='mt-14'>
                    <h2 id='cookies' className={headingClass}>
                        Cookies and analytics
                    </h2>

                    <p className={paragraphClass}>
                        This site does not use advertising or tracking cookies. We count visits with privacy-friendly analytics that works without cookies and does not identify you personally, so there is no cookie banner to dismiss.
                    </p>
                </section>

                <section aria-labelledby='terms' className='mt-14'>
                    <h2 id='terms' className={headingClass}>
                        Terms of use
                    </h2>

                    <p className={paragraphClass}>
                        The content on this site describes the Fieldhand platform and is provided for general information. Free trials and paid plans are governed by the agreement you accept when your workspace is created.
                    </p>

                    <ul className={listClass}>
                        <li>Figures such as hours saved and accuracy are averages across Fieldhand teams. Your results will depend on your workflows.</li>
                        <li>Product names and logos shown on this site belong to their respective owners and illustrate the tools Fieldhand connects to.</li>
                        <li>We may update these terms from time to time. The date at the top of this page shows the latest revision.</li>
                    </ul>
                </section>
            </article>
        </Layout>
    )
}

export default Legal
